/**
 * @ward3/frontend-shared/api — axios client factory partagé.
 *
 * Extrait 2026-07-06 (#17-A revue 2026-06). Consolide les 2 copies de
 * `src/auth/api.js` (SOC = 108 LOC, admin = 73 LOC) en un factory
 * paramétrable. Contrat préservé bit-à-bit — un consommateur ne doit
 * pas voir de différence de comportement.
 *
 * ## Différences ancien monde → factory
 *
 * SOC (`frontend/src/auth/api.js`) portait en plus :
 *   - `setMsspScopeOverride(uuid)` / `setTenantScopeOverride(uuid)` et
 *     l'injection des headers `X-Mssp-Scope` / `X-Tenant-Scope` sur
 *     toute requête non-`/auth/*`. Le backend valide via
 *     `require_scope_context` middleware.
 *   - Normalisation `"all"` (case-insensitive) → `null` sur le wire.
 *
 * Admin (`frontend-admin/src/auth/api.js`) n'en avait pas — le scope
 * cible passe par les paths URL (`/mssp/:id/tenant/:tid/…`).
 *
 * Le factory retourne les helpers scope UNIQUEMENT quand
 * `withScopeOverrides: true`. Ça évite d'exposer côté admin des setters
 * qui n'auraient rien à écouter.
 */

import axios from "axios";

/**
 * Crée un client axios authentifié + les helpers state.
 *
 * @param {object} [opts]
 * @param {string} [opts.baseURL]  Base URL des appels. Défaut : `import.meta.env.VITE_API_URL ?? ""`
 *                                 (vide → URLs relatives → same-origin → cookie refresh HttpOnly traverse).
 *                                 **Le same-origin est le seul déploiement supporté** : une base absolue
 *                                 casse la silent-refresh (cookie SameSite=Lax non envoyé cross-origin,
 *                                 QUICKSTART piège #4) ET est bloquée par la CSP `connect-src 'self'`
 *                                 servie par `frontend/nginx.conf`. Élargir la CSP ne suffirait pas :
 *                                 il faudrait aussi régler le cookie.
 * @param {boolean} [opts.withScopeOverrides=false]  Active les headers X-Mssp-Scope / X-Tenant-Scope
 *                                                    et retourne les setters correspondants. À `true` pour
 *                                                    la SPA SOC uniquement.
 * @param {number} [opts.timeout=10_000]  Timeout axios en ms.
 * @returns {{ api: import("axios").AxiosInstance,
 *             setAccessToken: (token: string|null) => void,
 *             getAccessToken: () => string|null,
 *             setMsspScopeOverride?: (uuid: string|null) => void,
 *             setTenantScopeOverride?: (uuid: string|null) => void }}
 */
export function createApiClient(opts = {}) {
  const {
    baseURL = import.meta.env?.VITE_API_URL ?? "",
    withScopeOverrides = false,
    timeout = 10_000,
  } = opts;

  const api = axios.create({
    baseURL,
    withCredentials: true,
    timeout,
  });

  // État en mémoire : jamais dans localStorage (XSS-safe).
  let accessToken = null;
  let refreshPromise = null;
  let msspScopeOverride = null;
  let tenantScopeOverride = null;

  function setAccessToken(token) {
    accessToken = token;
  }

  function getAccessToken() {
    return accessToken;
  }

  function setMsspScopeOverride(uuid) {
    // `"all"` (case-insensitive) est normalisé à `null` côté wire : le
    // backend accepte "all" pour X-Tenant-Scope mais on garde une
    // requête propre.
    msspScopeOverride =
      uuid && String(uuid).toLowerCase() !== "all" ? uuid : null;
  }

  function setTenantScopeOverride(uuid) {
    tenantScopeOverride =
      uuid && String(uuid).toLowerCase() !== "all" ? uuid : null;
  }

  // ── Request interceptor : Bearer + éventuels scope headers ─────────────
  api.interceptors.request.use((config) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    if (withScopeOverrides && config.url && !config.url.startsWith("/auth/")) {
      if (msspScopeOverride) config.headers["X-Mssp-Scope"] = msspScopeOverride;
      if (tenantScopeOverride)
        config.headers["X-Tenant-Scope"] = tenantScopeOverride;
    }
    return config;
  });

  // ── Response interceptor : silent refresh sur 401 ──────────────────────
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      if (
        error.response?.status === 401 &&
        !original._retry &&
        !original.url.includes("/auth/refresh") &&
        !original.url.includes("/auth/login")
      ) {
        original._retry = true;
        try {
          // Dedup des tentatives concurrentes de refresh.
          if (!refreshPromise) {
            refreshPromise = api.post("/auth/refresh").finally(() => {
              refreshPromise = null;
            });
          }
          const { data } = await refreshPromise;
          accessToken = data.access_token;
          original.headers.Authorization = `Bearer ${accessToken}`;
          return api(original);
        } catch (refreshErr) {
          accessToken = null;
          // AuthContext (dans chaque app) écoute `anm:auth-expired`
          // pour reset son state + rediriger vers /login.
          window.dispatchEvent(new CustomEvent("anm:auth-expired"));
          throw refreshErr;
        }
      }
      throw error;
    },
  );

  const client = {
    api,
    setAccessToken,
    getAccessToken,
  };

  if (withScopeOverrides) {
    client.setMsspScopeOverride = setMsspScopeOverride;
    client.setTenantScopeOverride = setTenantScopeOverride;
  }

  return client;
}
